import { EXTEND, IS, IS_NOT, nil, NO, UIRoute, UIView, UIViewController, YES } from "uicore-ts"
import { CompanyViewController } from "../CompanyView/CompanyViewController"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHBreadcrumbView, type RHBreadcrumbItem } from "../Custom components/RH/RHBreadcrumbView"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHSegmentedTabBarView } from "../Custom components/RH/RHSegmentedTabBarView"
import { RHButtons, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { UserProfileViewController } from "../UserProfileViewController"


type SettingsTab = "profile" | "company"

const SettingsTabKey: Record<SettingsTab, SettingsTab> = {
    profile: "profile",
    company: "company"
}


type SettingsSourceProcurement = {
    procurementId: string
    referenceNumber?: string
    backToComponentName?: string
}


/**
 * SettingsViewController — the "Seaded" screen reached from the user menu.
 * Owns the Minu konto / Ettevõte tab row and swaps only the child content;
 * each tab mounts an existing full screen (UserProfileViewController,
 * CompanyViewController) rather than duplicating their content. Both keep
 * reading their own route component regardless of nesting - handleRoute
 * propagates to the whole mounted child subtree automatically.
 */
export class SettingsViewController extends UIViewController {

    static override readonly routeComponentName = "account"

    static override readonly ParameterIdentifierName = {
        tab: "tab"
    }


    readonly breadcrumbView = new RHBreadcrumbView().addedAsSubviewToView(this.view)

    readonly sourceProcurementBackButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "← Tagasi hankele" },
            variant: "quietWarning",
            contentPadding: RHButtons.compactContentPadding,
            hidden: YES,
            style: { fontSize: RHButtons.compactFontSize },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => this._returnToInvitationSource()
            }
        })


    readonly tabBar = new RHSegmentedTabBarView([
        { key: SettingsTabKey.profile, title: "Minu konto" },
        { key: SettingsTabKey.company, title: "Ettevõte" }
    ]).configuredWithObject({
        selectionDidChange: (key) => {
            this._showTab(key as SettingsTab)
        }
    }).addedAsSubviewToView(this.view)

    readonly contentPanelView = new UIView().addedAsSubviewToView(this.view).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            if (IS(this.contentPanelView.subviews.firstElement)) {
                this.contentPanelView.subviews.firstElement.frame =
                    this.contentPanelView.bounds.rectangleWithHeight(
                        this.contentPanelView.subviews.firstElement
                    )
            }
        })
    })

    _profileViewController?: UserProfileViewController
    _companyViewController?: CompanyViewController

    override _focusManager = new RHFocusManager(() => [
        this.breadcrumbView,
        this.sourceProcurementBackButton,
        this.tabBar
    ])


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager, { focusesFirstItem: NO })
    }

    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
    }


    static breadcrumbItemsForRoute(route: UIRoute): RHBreadcrumbItem[] {
        const settingsRoute = route.routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(SettingsViewController, { tab: SettingsTabKey.profile })
        const items: RHBreadcrumbItem[] = [{ title: "Seaded", route: settingsRoute }]
        const tab = route.componentWithViewController(SettingsViewController)?.parameters.tab
        if (tab === SettingsTabKey.company || (!tab && route.componentWithViewController(CompanyViewController))) {
            const companyRoute = settingsRoute.routeWithViewControllerComponent(SettingsViewController, {
                tab: SettingsTabKey.company
            }).routeWithViewControllerComponent(CompanyViewController, { tab: "affiliation" })
            items.push({ title: "Ettevõte", route: companyRoute })
            const companyTab = CompanyViewController.tabInRoute(route)
            if (companyTab === "administration") {
                items.push({ title: "Liikmete haldus" })
            }
            else if (companyTab === "cpvInterests") {
                items.push({ title: "Leitavus ja hanketeavitused" })
            }
        }
        else {
            items.push({ title: "Minu konto" })
        }
        return items
    }


    get profileViewController(): UserProfileViewController {
        if (IS_NOT(this._profileViewController)) {
            this._profileViewController = new UserProfileViewController(new UIView("SettingsProfileView"))
        }
        return this._profileViewController
    }

    get companyViewController(): CompanyViewController {
        if (IS_NOT(this._companyViewController)) {
            this._companyViewController = new CompanyViewController(new UIView("SettingsCompanyView"))
        }
        return this._companyViewController
    }


    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
    }


    // ── Routing / tab state ─────────────────────────────────────────────────

    _tabForRoute(route: UIRoute): SettingsTab {
        const tabParameter = route.componentWithViewController(SettingsViewController)
            ?.parameters[SettingsViewController.ParameterIdentifierName.tab]
        if (tabParameter === SettingsTabKey.company) {
            return SettingsTabKey.company
        }
        return SettingsTabKey.profile
    }


    _showTab(tab: SettingsTab) {
        if (tab === this._tabForRoute(UIRoute.currentRoute)) {
            return
        }
        UIRoute.currentRoute
            .routeBySettingParameterInComponent(
                SettingsViewController.routeComponentName,
                SettingsViewController.ParameterIdentifierName.tab,
                tab
            )
            .apply()
    }


    override async handleRoute(route: UIRoute) {
        if (IS_NOT(route.componentWithViewController(SettingsViewController))) {
            return
        }

        const tab = this._tabForRoute(route)
        await this._mountChildViewControllerForTab(tab)
        this.tabBar.selectedKey = tab
        this._updateNavigationContextForRoute(route)

        await super.handleRoute(route)
    }


    _updateNavigationContextForRoute(route: UIRoute) {
        this.breadcrumbView.items = SettingsViewController.breadcrumbItemsForRoute(route)

        const sourceProcurement = this._sourceProcurementInRoute(route)
        const wasOpenedFromNewProcurement =
            CompanyViewController.isMemberInvitationFromNewProcurementInRoute(route)
        if (sourceProcurement || wasOpenedFromNewProcurement) {
            this.sourceProcurementBackButton.hidden = NO
            this.sourceProcurementBackButton.addedAsSubviewToView(this.view)
        }
        else {
            this.sourceProcurementBackButton.hidden = YES
            this.sourceProcurementBackButton.removeFromSuperview()
        }
        if (wasOpenedFromNewProcurement) {
            this.sourceProcurementBackButton.titleLabel.text = "← Tagasi"
        }
        else if (sourceProcurement?.referenceNumber) {
            this.sourceProcurementBackButton.titleLabel.text =
                `← Tagasi hankele ${sourceProcurement.referenceNumber}`
        }
        else {
            this.sourceProcurementBackButton.titleLabel.text = "← Tagasi hankele"
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _returnToInvitationSource() {
        if (CompanyViewController.isMemberInvitationFromNewProcurementInRoute(UIRoute.currentRoute)) {
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithComponent("user_main", { tab: "new" })
                .apply()
            return
        }
        this._returnToSourceProcurement()
    }


    _returnToSourceProcurement() {
        const sourceProcurement = this._sourceProcurementInRoute(UIRoute.currentRoute)
        if (!sourceProcurement) {
            return
        }
        const procurementDetailsParameters: Record<string, string> = {
            id: sourceProcurement.procurementId,
            tab: "team"
        }
        if (sourceProcurement.backToComponentName) {
            procurementDetailsParameters.backTo = sourceProcurement.backToComponentName
        }
        UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithComponent("procurement_details", procurementDetailsParameters)
            .apply()
    }


    // Reads the "invite a member" source-procurement context that
    // ProcurementDetailsViewController._routeForCompanyMemberInvitation encoded
    // on the "company" route component, so the breadcrumb/back button can
    // reference it without depending on CompanyViewController being mounted.
    _sourceProcurementInRoute(route: UIRoute): SettingsSourceProcurement | undefined {
        const parameters = route.componentWithName(CompanyViewController.routeComponentName)?.parameters
        const procurementId = parameters?.sourceProcurementId
        if (!procurementId) {
            return undefined
        }
        return {
            procurementId,
            referenceNumber: parameters.sourceProcurementReferenceNumber,
            backToComponentName: parameters.sourceProcurementBackTo
        }
    }


    // ── Child VC management ─────────────────────────────────────────────────

    async _mountChildViewControllerForTab(tab: SettingsTab) {
        if (tab === SettingsTabKey.profile) {
            this._removeChildIfMounted(this._companyViewController)
            await this._mountChildIfNeeded(this.profileViewController)
        }
        else {
            this._removeChildIfMounted(this._profileViewController)
            await this._mountChildIfNeeded(this.companyViewController)
        }

        this.view.setNeedsLayoutUpToRootView()
    }


    async _mountChildIfNeeded(viewController: UIViewController) {
        if (!this.hasChildViewController(viewController)) {
            this.addChildViewControllerInContainer(viewController, this.contentPanelView)
            await viewController.viewWillAppear()
            viewController.viewDidAppear().then(nil)
        }
    }


    _removeChildIfMounted(viewController: UIViewController | undefined) {
        if (IS(viewController) && this.hasChildViewController(viewController)) {
            viewController.viewWillDisappear().then(nil)
            this.removeChildViewController(viewController)
        }
    }


    // ── Layout ──────────────────────────────────────────────────────────────

    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        const breadcrumbRow = bounds.rectangleWithHeight(this.breadcrumbView)

        breadcrumbRow
            .assignedAsFrameOfView(this.breadcrumbView)

        const navigationRow = breadcrumbRow
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
            .rectangleWithMaxWidth(RHCompanyViewTokens.administrationContentMaxWidth, 0.5)
        const navigationSideColumnWidth = RHButtons.pageBackButtonWidth + RHSpacing.medium

        navigationRow
            .IF(!this.sourceProcurementBackButton.hidden)
            .rectangleWithWidth(RHButtons.pageBackButtonWidth)
            .assignedAsFrameOfView(this.sourceProcurementBackButton)
            .ELSE()
            .ENDIF()

        navigationRow
            .IF(!this.sourceProcurementBackButton.hidden)
            .rectangleWithInsets(
                navigationSideColumnWidth,
                navigationSideColumnWidth,
                0,
                0
            )
            .ELSE()
            .ENDIF()
            .assignedAsFrameOfView(this.tabBar)
            .rectangleForNextRow(padding, this.contentPanelView)
            .rectangleWithX(bounds.x)
            .rectangleWithWidth(bounds.width)
            .assignedAsFrameOfView(this.contentPanelView)
    }

}
